import { Loader } from "@circle-react-shared/uikit/Loader";
import { Navigate } from "@tanstack/react-router";
import { isPast, addMinutes } from "date-fns";
import { useState, useEffect } from "react";

import { confirmationRoute } from "@/react/routes/PaywallCheckout/tanstack_routes/checkout/$checkoutPath/confirmation/$sessionId";
import { checkoutSessionStatuses } from "@/react/types/Paywalls/Checkout/Session";

import { useCheckoutSession } from "../../Checkout/contexts/CheckoutSessionContext";
import { canAccessConfirmation, getRedirectPath } from "../helpers/RouteGuards";
import { useGoToNextStep } from "../hooks/useGoToNextStep";
import { ConfirmationPage } from "../pages/ConfirmationPage";
import { FadeEffect } from "../pages/OfferPage/components/FadeEffect";

export const ConfirmationRoute = () => {
  const { checkoutPath } = confirmationRoute.useParams();
  const { session, isLoading } = useCheckoutSession();
  const { redirectAfterConfirmation } = useGoToNextStep();

  const [shouldShowLoader, setShouldShowLoader] = useState(false);

  // Showing Loader after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldShowLoader(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return shouldShowLoader ? (
      <FadeEffect>
        <Loader />
      </FadeEffect>
    ) : null;
  }

  // If the Confirmation passes 30 minutes threshold, the member is redirected.
  // No GA4 push here — the purchase event already fired when the member first
  // completed checkout and is protected by sessionStorage dedup in
  // ga4PurchaseHelper. A revisit of a stale completed-session URL must not
  // re-fire it; redirecting before ConfirmationPage renders ensures the helper
  // is never invoked on this path.
  if (session && session.status === checkoutSessionStatuses.completed) {
    const confirmationThreshold = addMinutes(new Date(session.created_at), 30);

    if (isPast(confirmationThreshold)) {
      redirectAfterConfirmation(session);
      return null;
    }
  }

  if (session && !canAccessConfirmation(session)) {
    return <Navigate to={getRedirectPath(session, checkoutPath)} />;
  }

  return <ConfirmationPage />;
};
